import { cn } from '$lib/lib/cn';
import type { RunStatus } from './StatusPill';

// Graduated severity scale used by status dots, portal card borders, and the
// dashboard header. Maps to existing semantic colour tokens — keeping a single
// source of truth in tokens.css rather than adding parallel severity vars.
//   healthy  -> positive  (green)
//   degraded -> warning   (yellow / amber)
//   failed   -> danger    (red)
//   unknown  -> fg-subtle (grey, never-run / pending)
export type Severity = 'healthy' | 'degraded' | 'failed' | 'unknown';

export function severityFromStatus(status: RunStatus): Severity {
  switch (status) {
    case 'done':
      return 'healthy';
    case 'done-with-errors':
    case 'paused':
      return 'degraded';
    case 'failed':
      return 'failed';
    case 'pending':
    case 'running':
    case 'cancelled':
      return 'unknown';
  }
}

const dotTone: Record<Severity, string> = {
  healthy: 'bg-positive',
  degraded: 'bg-warning',
  failed: 'bg-danger',
  unknown: 'bg-fg-subtle'
};

const ringTone: Record<Severity, string> = {
  healthy: 'border-positive/30',
  degraded: 'border-warning/35',
  failed: 'border-danger/30',
  unknown: 'border-rule-strong'
};

const headerStripTone: Record<Severity, string> = {
  healthy: 'bg-positive/15',
  degraded: 'bg-warning/15',
  failed: 'bg-danger/15',
  unknown: 'bg-surface-2'
};

export interface StatusDotProps {
  severity: Severity;
  pulse?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  // When provided, exposes the dot to assistive tech; otherwise it's decorative.
  ariaLabel?: string;
}

export function StatusDot({ severity, pulse = false, size = 'sm', className, ariaLabel }: StatusDotProps) {
  const dim = size === 'md' ? 'h-2 w-2' : 'h-1.5 w-1.5';
  return (
    <span
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      className={cn(
        'inline-block shrink-0 rounded-full',
        dim,
        dotTone[severity],
        pulse && severity !== 'healthy' && 'motion-safe:animate-pulse',
        className
      )}
    />
  );
}

// Helpers exported so portal cards / dashboard header can use the same
// graduated colour scheme without duplicating Tailwind class strings.
export function severityCardBorder(severity: Severity): string {
  return cn('border', ringTone[severity]);
}

export function severityHeaderStrip(severity: Severity): string {
  return headerStripTone[severity];
}
